import { useEffect, useState } from 'react';
import { companyApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import type { ActivityEvent } from '../lib/types.ts';

export function CompanyActivity() {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  async function load(beforeId: number | null) {
    try {
      const res = await companyApi.get<{ events: ActivityEvent[]; next_cursor: number | null }>(
        '/api/me/activity',
        { query: { limit: 50, before_id: beforeId ?? undefined } },
      );
      setEvents((prev) => (beforeId === null ? res.events : [...prev, ...res.events]));
      setCursor(res.next_cursor);
      setHasMore(res.next_cursor !== null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    void load(null);
  }, []);

  if (loading) return <CenteredSpinner />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Activity</h1>
        <p className="mt-1 text-sm text-slate-500">Recent events on your compliance.</p>
      </div>
      <ErrorMessage error={error} />
      {events.length === 0 ? (
        <EmptyState title="No activity yet" />
      ) : (
        <div className="space-y-2">
          {events.map((e) => (
            <div key={e.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-900">{eventLabel(e.event_type)}</span>
                <span className="text-xs text-slate-500">{new Date(e.occurred_at).toLocaleString()}</span>
              </div>
              <div className="mt-1 text-xs text-slate-500">by {e.actor_type}</div>
            </div>
          ))}
          {hasMore && (
            <div className="pt-3 text-center">
              <Btn
                variant="secondary"
                loading={loadingMore}
                onClick={() => {
                  setLoadingMore(true);
                  void load(cursor);
                }}
              >
                Load more
              </Btn>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function eventLabel(type: string): string {
  return type
    .split(/[._-]/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}
